import constants from '@Common/Core/Constant';
import { DayOfWeek, TimeInterval, TimePeriod } from '@Common/Enums';
import { ChartDateType, RoundIntervalType } from '@Common/Enums/Enum';
import { comparers } from '@Common/Helpers/Comparers';
import localizationHelper from '@Common/Helpers/LocalizationHelper';
import { fromServerTime, toServerTime } from '@Common/Helpers/ServerTime';
import { BaseDrillDown, DateTimeDuration, DateTimeInterval, KPISettingDefinition, TimeDrillDown, TimeSettings, ViewerModel } from '@InstanceFactory';
import FormatDotNetStyle from '@Libs/FormatDotNetStyle';

import Maybe from '../Core/Maybe';
import documentHelper from './DocumentHelper';

namespace timeHelper {
    export function dateTimeFromString(str: string): Date {
        const items = str.split(' ');
        if (items.length === 2) {
            const [dateStr, timeStr] = items;
            const toInt = _.partial(parseInt, _, 10);
            const dateItems = dateStr.split('/').map(toInt);
            const timeItems = timeStr.split(':').map(toInt);
            if (dateItems.length === 3 && timeItems.length === 3) {
                return new Date(dateItems[2], dateItems[0] - 1, dateItems[1], timeItems[0], timeItems[1], timeItems[2]);
            }
        }
        return new Date();
    }

    export function dateTimeInInterval(date: Date, interval: DateTimeInterval): boolean {
        return interval.from <= date && date <= interval.to;
    }

    export function getFirstDayOfWeek(): DayOfWeek {
        return Maybe(altosoft.model as ViewerModel)
            .read(m => m.insightMeta)
            .read(m => m.dateTimeFormat)
            .read(dtf => dtf.firstDayOfWeek)
            .valueOrDefault(DayOfWeek.sunday);
    }

    export function nearestStartTime(date: Date, intervalType: TimeInterval, firstDayOfWeek = getFirstDayOfWeek(), financialYearStart?: Date): Date {
        const startTime = new Date(date.getTime());
        let found = false;
        if (intervalType === TimeInterval.year1) {
            if (financialYearStart) {
                const year =
                    financialYearStart.getMonth() > startTime.getMonth() ||
                    (financialYearStart.getMonth() === startTime.getMonth() && financialYearStart.getDate() > startTime.getDate())
                        ? startTime.getFullYear() - 1
                        : startTime.getFullYear();

                return new Date(year, financialYearStart.getMonth(), 1);
            } else {
                startTime.setMonth(0);
            }
            found = true;
        }
        if (intervalType === TimeInterval.quarter1) {
            if (financialYearStart) {
                const start = financialYearStart.getMonth() % 3;
                const month = Math.floor(((startTime.getMonth() - start + 12) % 12) / 3) * 3 + start;
                return new Date(month - startTime.getMonth() > 1 ? startTime.getFullYear() - 1 : startTime.getFullYear(), month, 1);
            } else {
                const month = startTime.getMonth() % 3;
                startTime.setMonth(startTime.getMonth() - month);
            }
            found = true;
        }
        if (intervalType === TimeInterval.month1 || intervalType === TimeInterval.year1) {
            startTime.setDate(1);
            found = true;
        }
        if (intervalType === TimeInterval.week1) {
            const firstDayOfWeekIndex = _.findIndex(DayOfWeek.getValues(), ['name', firstDayOfWeek]); // TODO: why name ???
            const dayDiff = (7 + startTime.getDay() - firstDayOfWeekIndex) % 7;
            startTime.setDate(startTime.getDate() - dayDiff);
            found = true;
        }
        if (intervalType === TimeInterval.day1 || found) {
            startTime.setHours(0);
            found = true;
        }
        if (intervalType === TimeInterval.hour1 || found) {
            startTime.setMinutes(0);
            found = true;
        }
        if (intervalType === TimeInterval.minute1 || found) {
            startTime.setSeconds(0);
            found = true;
        }
        if (intervalType === TimeInterval.second1 || found) {
            startTime.setMilliseconds(0);
        }

        return startTime;
    }

    export function isTimeDrillDown(dimensionId: string): dimensionId is TimeInterval {
        return (intervalTypesByValue[dimensionId] && intervalTypesByValue[dimensionId].isTime) || false;
    }

    export function isTimeDrill(drill: BaseDrillDown): drill is TimeDrillDown {
        return drill && isTimeDrillDown(drill.id);
    }

    export function getTimeIntervals(params: ShowTimeIntervalParam): { id: TimeInterval; value: string }[] {
        return _.map(getTimeDrillDownIntervals(params), timeIntervalType => getTimeInterval(timeIntervalType));
    }

    export function getTimeInterval(intervalType: TimeInterval): { id: TimeInterval; value: string } {
        return { id: intervalType, value: getIntervalString(intervalType, true) };
    }

    export function getIntervalString(dimensionId: string, plural = false): string {
        const desc = intervalTypesByValue[dimensionId];
        if (desc) {
            return plural ? desc.pluralName : desc.name;
        }
        return plural ? strings.timeDrillDownType_plural.years : strings.timeDrillDownType.year;
    }

    export function getAlignedIntervalTicks(intervalType: TimeInterval, intervalCount: number, startDate: Date): number {
        return addInterval(startDate.getTime(), intervalType, intervalCount) - startDate.getTime();
    }

    export function addInterval(time: Date, intervalType: TimeInterval, intervalCount: number): Date;
    export function addInterval(time: number, intervalType: TimeInterval, intervalCount: number): number;
    export function addInterval(time: number | Date, intervalType: TimeInterval, intervalCount: number): number | Date {
        const endDate = new Date(_.isDate(time) ? time.getTime() : time);
        if (TimeInterval.second1 === intervalType) {
            endDate.setSeconds(endDate.getSeconds() + intervalCount);
        }
        if (TimeInterval.minute1 === intervalType) {
            endDate.setMinutes(endDate.getMinutes() + intervalCount);
        }
        if (TimeInterval.hour1 === intervalType) {
            endDate.setHours(endDate.getHours() + intervalCount);
        }
        if (TimeInterval.day1 === intervalType) {
            endDate.setDate(endDate.getDate() + intervalCount);
        }
        if (TimeInterval.week1 === intervalType) {
            endDate.setDate(endDate.getDate() + 7 * intervalCount);
        }
        if (TimeInterval.month1 === intervalType) {
            endDate.setMonth(endDate.getMonth() + intervalCount);
        }
        if (TimeInterval.quarter1 === intervalType) {
            endDate.setMonth(endDate.getMonth() + 3 * intervalCount);
        }
        if (TimeInterval.year1 === intervalType) {
            endDate.setFullYear(endDate.getFullYear() + intervalCount);
        }

        return _.isDate(time) ? endDate : endDate.getTime();
    }

    export function getIntervalEnd(
        startDate: Date,
        intervalType: TimeInterval,
        intervalCount = 1,
        firstDayOfWeek?: DayOfWeek,
        financialYearStart?: Date
    ): Date {
        const startTime = nearestStartTime(startDate, intervalType, firstDayOfWeek, financialYearStart);
        return addInterval(startTime, intervalType, intervalCount);
    }

    export function getTimeIntervalOrder(timeDrilldownId: string): number {
        return (intervalTypesByValue[timeDrilldownId] && intervalTypesByValue[timeDrilldownId].order) || 20;
    }

    function roundIntervalByTimeSetting(interval: DateTimeInterval, timeSettings: TimeSettings): DateTimeInterval {
        const firstDayOfWeek = getFirstDayOfWeek();
        const financialYearStart = getFinancialYearStart(timeSettings);
        if (timeSettings.roundIntervalType === RoundIntervalType.right) {
            interval.from = getIntervalEnd(interval.from, timeSettings.roundTimeIntervalType, 1, firstDayOfWeek, financialYearStart);
        }
        if (timeSettings.roundIntervalType === RoundIntervalType.left) {
            interval.from = nearestStartTime(interval.from, timeSettings.roundTimeIntervalType, firstDayOfWeek, financialYearStart);
        }
        if (timeSettings.roundEndPointIntervalType === RoundIntervalType.right) {
            interval.to = getIntervalEnd(interval.to, timeSettings.roundEndPointTimeIntervalType, 1, firstDayOfWeek, financialYearStart);
        }
        if (timeSettings.roundEndPointIntervalType === RoundIntervalType.left) {
            interval.to = nearestStartTime(interval.to, timeSettings.roundEndPointTimeIntervalType, firstDayOfWeek, financialYearStart);
        }
        return interval;
    }

    export function getTimeSettingsInterval(
        timeSettings: TimeSettings,
        metrics: KPISettingDefinition[] = null,
        includeRounding = false
    ): DateTimeInterval {
        let result = DateTimeInterval.create();
        const firstDayOfWeek = getFirstDayOfWeek();
        const financialYearStart = getFinancialYearStart(timeSettings);
        let clientTime = new Date();
        if (timeSettings.chartDateType === ChartDateType.lastToCurrentTime || timeSettings.chartDateType === ChartDateType.lastToAvailableKpiDate) {
            if (timeSettings.chartDateType === ChartDateType.lastToAvailableKpiDate) {
                const metric = documentHelper.findMetric(timeSettings.metric, metrics, []);
                clientTime = metric && metric.lastAvailableDate ? metric.lastAvailableDate : timeSettings.interval.to;
            }
            const alignedIntervalTicks = getAlignedIntervalTicks(
                timeSettings.duration.intervalType,
                -timeSettings.duration.intervalCount,
                clientTime
            );
            result.to = clientTime;
            result.from = new Date(clientTime.getTime() + alignedIntervalTicks);
            if (includeRounding) {
                result = roundIntervalByTimeSetting(result, timeSettings);
            }
        } else if (timeSettings.chartDateType === ChartDateType.onGoing || timeSettings.chartDateType === ChartDateType.metricOnGoing) {
            result.from = nearestStartTime(clientTime, timeSettings.duration.intervalType, firstDayOfWeek, financialYearStart);
            const alignedIntervalTicks = getAlignedIntervalTicks(timeSettings.duration.intervalType, 1, result.from);
            result.to = timeSettings.toCurrentDate ? clientTime : new Date(result.from.getTime() + alignedIntervalTicks);
            if (timeSettings.previous) {
                const oldFrom = timeSettings.interval.from;
                const milliseconds = getAlignedIntervalTicks(timeSettings.duration.intervalType, -1, timeSettings.interval.from);
                result.from = new Date(getMilliseconds(timeSettings.interval.from) + milliseconds);
                result.to = timeSettings.toCurrentDate
                    ? new Date(timeSettings.interval.from.getTime() + clientTime.getTime() - oldFrom.getTime())
                    : oldFrom;
            }
        } else if (timeSettings.chartDateType === ChartDateType.dateRange) {
            result.from = new Date(timeSettings.interval.from);
            result.to = new Date(timeSettings.interval.to);
        }
        return result;
    }

    export function updateTimeSettings(projectId: string, timeSettings: TimeSettings): void {
        const metrics = documentHelper.getMetrics(projectId);
        const interval = getTimeSettingsInterval(timeSettings, metrics, true);
        if (interval) {
            timeSettings.interval = interval;
        }
    }

    export function compareDurationTypes(durationType1: string, durationType2: string): number {
        const desc1 = intervalTypesByValue[durationType1];
        const desc2 = intervalTypesByValue[durationType2];
        if (desc1 && desc2 && desc1.isTime && desc2.isTime) {
            return comparers.comparer(desc1.order, desc2.order);
        }
        return 0;
    }

    export function getAvailableComponentDurations(): { duration: DateTimeDuration, frequency: TimeInterval }[] {
        return [
            { duration: dateTimeDuration(TimeInterval.day1), frequency: TimeInterval.hour1 },
            { duration: dateTimeDuration(TimeInterval.day1, 10), frequency: TimeInterval.day1 },
            { duration: dateTimeDuration(TimeInterval.month1), frequency: TimeInterval.day1 },
            { duration: dateTimeDuration(TimeInterval.month1, 3), frequency: TimeInterval.week1 },
            { duration: dateTimeDuration(TimeInterval.month1, 6), frequency: TimeInterval.month1 },
            { duration: dateTimeDuration(TimeInterval.year1), frequency: TimeInterval.month1 },
            { duration: dateTimeDuration(TimeInterval.year1, 3), frequency: TimeInterval.quarter1 },
            { duration: dateTimeDuration(TimeInterval.year1, 5), frequency: TimeInterval.year1 },
            { duration: dateTimeDuration(TimeInterval.year1, 10), frequency: TimeInterval.year1 }
        ];
    }

    export function getDefaultTimeDrillDown(drilldowns: BaseDrillDown[], componentInterval: DateTimeInterval): TimeDrillDown {
        const frequencies = getFrequencies(componentInterval);
        const compatibleIntervals = frequencies.compatibleFrequencies;
        const preferredInterval = frequencies.preferredFrequency;

        const usedTimeDrills = drilldowns.filter(drill => drill.inUse && TimeDrillDown.isInstanceOfType(drill)) as TimeDrillDown[];

        function getFirstTimeDrill(frequency: TimeInterval) {
            return usedTimeDrills.filter(drill => drill.frequency === frequency)[0];
        }

        let drillDown = getFirstTimeDrill(preferredInterval);

        if (drillDown == null) {
            drillDown = _(compatibleIntervals).map(getFirstTimeDrill).compact().first() || null;
        }

        if (drillDown == null) {
            const maxCompatible = _(compatibleIntervals).orderBy(getTimeIntervalOrder).last();
            const maxOrderPosition = getTimeIntervalOrder(maxCompatible);

            drillDown = _(usedTimeDrills)
                .orderBy(drill => getTimeIntervalOrder(drill.frequency))
                .find(drill => getTimeIntervalOrder(drill.frequency) > maxOrderPosition);
        }
        return drillDown;
    }

    export function getDefaultTimeBreakdown(drilldowns: BaseDrillDown[], interval: DateTimeInterval): BaseDrillDown {
        const drillDown = _.find(drilldowns, drill => drill.chartDimensionId === constants.dimension1 && drill.inUse);
        if (drillDown && isTimeDrill(drillDown)) {
            const frequencies = getFrequencies(interval);
            if (!frequencies.compatibleFrequencies.contains(drillDown.frequency)) {
                return getDefaultTimeDrillDown(drilldowns, interval);
            }
            return drillDown;
        }

        return undefined;
    }

    export function getFrequencies(
        interval: DateTimeInterval
    ): {
        compatibleFrequencies: TimeInterval[];
        preferredFrequency: TimeInterval;
    } {
        const startTicks = interval.from.valueOf();
        const end = interval.to.valueOf();

        const twoYearInterval = new Date(startTicks).setFullYear(interval.from.getFullYear() + 2);
        const sixMonthsInterval = new Date(startTicks).setMonth(interval.from.getMonth() + 6);
        const threeMonthsInterval = new Date(startTicks).setMonth(interval.from.getMonth() + 3);
        const twoMonthsInterval = new Date(startTicks).setMonth(interval.from.getMonth() + 2);
        const twentyEightDaysInterval = new Date(startTicks).setDate(interval.from.getDate() + 28);
        const fourteenDaysInterval = new Date(startTicks).setDate(interval.from.getDate() + 14);
        const twoDaysInterval = new Date(startTicks).setDate(interval.from.getDate() + 2);
        const twoHoursInterval = new Date(startTicks).setHours(interval.from.getHours() + 2);
        const oneMinuteInterval = new Date(startTicks).setMinutes(interval.from.getMinutes() + 1);

        if (end >= twoYearInterval) {
            return {
                compatibleFrequencies: [TimeInterval.year1, TimeInterval.quarter1, TimeInterval.month1],
                preferredFrequency: TimeInterval.year1
            };
        } else if (end >= sixMonthsInterval && end < twoYearInterval) {
            return {
                compatibleFrequencies: [TimeInterval.month1, TimeInterval.quarter1],
                preferredFrequency: TimeInterval.quarter1
            };
        } else if (end >= threeMonthsInterval && end < sixMonthsInterval) {
            return {
                compatibleFrequencies: [TimeInterval.week1, TimeInterval.month1],
                preferredFrequency: TimeInterval.month1
            };
        } else if (end >= twoMonthsInterval && end < threeMonthsInterval) {
            return {
                compatibleFrequencies: [TimeInterval.week1, TimeInterval.month1],
                preferredFrequency: TimeInterval.week1
            };
        } else if (end >= twentyEightDaysInterval && end < twoMonthsInterval) {
            return {
                compatibleFrequencies: [TimeInterval.day1, TimeInterval.week1],
                preferredFrequency: TimeInterval.week1
            };
        } else if (end >= fourteenDaysInterval && end < twentyEightDaysInterval) {
            return {
                compatibleFrequencies: [TimeInterval.day1, TimeInterval.week1],
                preferredFrequency: TimeInterval.day1
            };
        } else if (end >= twoDaysInterval && end < fourteenDaysInterval) {
            return { compatibleFrequencies: [TimeInterval.day1], preferredFrequency: TimeInterval.day1 };
        } else if (end >= twoHoursInterval && end < twoDaysInterval) {
            return { compatibleFrequencies: [TimeInterval.hour1], preferredFrequency: TimeInterval.hour1 };
        } else if (end > oneMinuteInterval && end < twoHoursInterval) {
            return {
                compatibleFrequencies: [TimeInterval.minute1],
                preferredFrequency: TimeInterval.minute1
            };
        } else {
            return {
                compatibleFrequencies: [TimeInterval.second1],
                preferredFrequency: TimeInterval.second1
            };
        }
    }

    export function getMinimumIntervalType(interval: DateTimeInterval): TimeInterval {
        const timeDrillDownIntervals = getTimeDrillDownIntervals();
        const fromMs = getMilliseconds(interval.from);
        const toMs = getMilliseconds(interval.to);

        for (let i = timeDrillDownIntervals.length - 1; i >= 0; i--) {
            const intervalType = timeDrillDownIntervals[i];
            const milliseconds = getAlignedIntervalTicks(intervalType, -1, interval.to);
            if (fromMs < toMs + milliseconds) {
                return intervalType;
            }
        }
        return TimeInterval.second1;
    }

    export function getTimeDrillDownIntervals(params: ShowTimeIntervalParam = new ShowTimeIntervalParam()): TimeInterval[] {
        params = _.defaults(params || {}, new ShowTimeIntervalParam());

        const intervals: TimeInterval[] = [];

        if (params.showSeconds) {
            intervals.push(TimeInterval.second1);
        }
        intervals.push(TimeInterval.minute1, TimeInterval.hour1, TimeInterval.day1);
        if (params.showWeeks) {
            intervals.push(TimeInterval.week1);
        }
        if (params.showMonths) {
            intervals.push(TimeInterval.month1);
        }
        if (params.showQuarters) {
            intervals.push(TimeInterval.quarter1);
        }
        if (params.showYears) {
            intervals.push(TimeInterval.year1);
        }

        return intervals;
    }

    export function durationEquals(d1: DateTimeDuration, d2: DateTimeDuration): boolean {
        return d1.intervalCount === d2.intervalCount && d1.intervalType === d2.intervalType;
    }

    export function getFrequency(duration: DateTimeDuration): TimeInterval {
        const availableDurations = getAvailableComponentDurations();
        const availableDuration = _.find(availableDurations, ad => durationEquals(ad.duration, duration));

        if (availableDuration) {
            return availableDuration.frequency;
        }

        for (const item of availableDurations) {
            if (
                compareDurationTypes(duration.intervalType, item.duration.intervalType) === -1 ||
                (duration.intervalType === item.duration.intervalType && duration.intervalCount <= item.duration.intervalCount)
            ) {
                return item.frequency;
            }
        }
        return _.last(availableDurations).frequency;
    }

    export function getTimeIntervalDuration(timeInterval: DateTimeInterval): DateTimeDuration {
        const intervalType = getIntervalType(timeInterval.from, timeInterval.to);
        const intervalCount = getIntervalCount(timeInterval.from, timeInterval.to, intervalType);

        return dateTimeDuration(intervalType, intervalCount);
    }

    export function getIntervalApproxLengthInMilliseconds(intervalType: TimeInterval): number {
        if (intervalType === TimeInterval.second1) {
            return 1000;
        }
        if (intervalType === TimeInterval.minute1) {
            return 1000 * 60;
        }
        if (intervalType === TimeInterval.hour1) {
            return 1000 * 3600;
        }
        if (intervalType === TimeInterval.day1) {
            return 1000 * 3600 * 24;
        }
        if (intervalType === TimeInterval.week1) {
            return 1000 * 3600 * 24 * 7;
        }
        if (intervalType === TimeInterval.month1) {
            return 1000 * 3600 * 24 * 30;
        }
        if (intervalType === TimeInterval.quarter1) {
            return 1000 * 3600 * 24 * 30 * 3;
        }
        if (intervalType === TimeInterval.year1) {
            return 1000 * 3600 * 24 * 30 * 12;
        }

        return 0;
    }

    export function getIntervalType(from: Date, to: Date): TimeInterval {
        const intervalsByOrder = _(intervalTypesByValue)
            .orderBy((desc: IntervalTypeDesc) => desc.order, 'desc')
            .filter(desc => desc.isTime)
            .map(desc => desc.value)
            .value();

        for (const intervalType of intervalsByOrder) {
            const newTo = addInterval(from, intervalType, 1);
            if (newTo <= to) {
                return intervalType;
            }
        }

        return TimeInterval.year1;
    }

    export function getMonths(from: Date, to: Date): number {
        const restMonths = to.getFullYear() !== from.getFullYear() ? 12 - from.getMonth() + to.getMonth() : to.getMonth() - from.getMonth();
        const yearsMonths = (to.getFullYear() - from.getFullYear() > 1 ? to.getFullYear() - from.getFullYear() - 1 : 0) * 12;
        const rest = from.getDate() < to.getDate() ? 1 : 0;

        return restMonths + yearsMonths + rest;
    }

    export function getMilliseconds(date: Date): number {
        return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getSeconds(), date.getMilliseconds());
    }

    export function getIntervalCount(from: Date, to: Date, intervalType: string): number {
        function devide(val: number, denum: number): number {
            const result = Math.ceil(val / denum);
            return result > 0 ? result : 1;
        }

        if (from > to) {
            return 0;
        }

        const ticks = getMilliseconds(to) - getMilliseconds(from);
        switch (intervalType) {
            case TimeInterval.second1:
                return devide(ticks, 1000);
            case TimeInterval.minute1:
                return devide(ticks, 60000);
            case TimeInterval.hour1:
                return devide(ticks, 3600000);
            case TimeInterval.day1:
                return devide(ticks, 86400000);
            case TimeInterval.week1:
                return devide(ticks, 604800000);
            case TimeInterval.month1:
                return getMonths(from, to);
            case TimeInterval.quarter1:
                return devide(getMonths(from, to), 3);
            case TimeInterval.year1:
                return devide(to.getFullYear() - from.getFullYear(), 1);
            default:
                return devide(ticks, 300000);
        }
    }

    export function getTimeDrillDownDisplayName(frequency: TimeInterval): string {
        return (intervalTypesByValue[frequency] && intervalTypesByValue[frequency].displayName) || 'Unknown time drilldown';
    }

    export function intersectIntervals(dateTimeIntervals: DateTimeInterval[]): DateTimeInterval | null {
        if (dateTimeIntervals.length === 0) {
            return null;
        }
        return dateTimeIntervals.reduce((p, c) => dateTimeInterval(p.from > c.from ? p.from : c.from, p.to < c.to ? p.to : c.to));
    }

    export function unionIntervals(dateTimeIntervals: DateTimeInterval[]): DateTimeInterval | null {
        if (dateTimeIntervals.length === 0) {
            return null;
        }
        return dateTimeIntervals.reduce((p, c) => dateTimeInterval(p.from < c.from ? p.from : c.from, p.to > c.to ? p.to : c.to));
    }

    export function equalIntervals(interval1: DateTimeInterval, interval2: DateTimeInterval): boolean {
        return interval1.from.valueOf() === interval2.from.valueOf() && interval1.to.valueOf() === interval2.to.valueOf();
    }

    export function getFinancialYearStart(timeSettings: TimeSettings): Date | null {
        if (!timeSettings.useCalendarYear) {
            const date = new Date();
            date.setMonth(altosoft.model.user.financialYearStartFromQuery || 0);
            date.setDate(1);
            return date;
        }
        return null;
    }

    export function parseDateTime(value: string, format: string, mustBeValid = false): Date {
        const formatter = new FormatDotNetStyle(localizationHelper.getTwoLettersLanguageName());

        if (value) {
            return formatter.parseDateTime(value, format, { withCheck: mustBeValid });
        }

        return null;
    }

    export function getADTicks(date: Date): number {
        return toServerTime(date).getTime() * 10000 + 621355968000000000;
    }

    export function getDateFromADTicks(ticks: number): Date {
        const date = new Date((ticks - 621355968000000000) / 10000);
        return fromServerTime(date);
    }

    export function timeFromSeconds(seconds: number): Date {
        const time = new Date();
        time.setHours(0);
        time.setMinutes(0);
        time.setSeconds(seconds);
        return time;
    }

    export function timeToSeconds(time: Date): number {
        return time.getSeconds() + time.getMinutes() * 60 + time.getHours() * 3600;
    }

    export function getPeriodSettings(timePeriod: TimePeriod): TimeSettings {
        const timeSettings = TimeSettings.create();
        switch (timePeriod) {
            case TimePeriod.day:
            case TimePeriod.pd:
                timeSettings.chartDateType = ChartDateType.onGoing;
                timeSettings.duration.intervalType = TimeInterval.day1;
                timeSettings.toCurrentDate = timePeriod === TimePeriod.day;
                timeSettings.previous = timePeriod === TimePeriod.pd;
                break;
            case TimePeriod.wtd:
            case TimePeriod.pw:
                timeSettings.chartDateType = ChartDateType.onGoing;
                timeSettings.duration.intervalType = TimeInterval.week1;
                timeSettings.toCurrentDate = timePeriod === TimePeriod.wtd;
                timeSettings.previous = timePeriod === TimePeriod.pw;
                break;
            case TimePeriod.l7D:
                timeSettings.chartDateType = ChartDateType.lastToCurrentTime;
                timeSettings.duration.intervalType = TimeInterval.day1;
                timeSettings.duration.intervalCount = 7;
                break;
            case TimePeriod.mtd:
            case TimePeriod.pm:
                timeSettings.chartDateType = ChartDateType.onGoing;
                timeSettings.duration.intervalType = TimeInterval.month1;
                timeSettings.toCurrentDate = timePeriod === TimePeriod.mtd;
                timeSettings.previous = timePeriod === TimePeriod.pm;
                break;
            case TimePeriod.l30D:
                timeSettings.chartDateType = ChartDateType.lastToCurrentTime;
                timeSettings.duration.intervalType = TimeInterval.day1;
                timeSettings.duration.intervalCount = 30;
                break;
            case TimePeriod.qtd:
            case TimePeriod.pq:
                timeSettings.chartDateType = ChartDateType.onGoing;
                timeSettings.duration.intervalType = TimeInterval.quarter1;
                timeSettings.toCurrentDate = timePeriod === TimePeriod.qtd;
                timeSettings.previous = timePeriod === TimePeriod.pq;
                break;
            case TimePeriod.ytd:
                timeSettings.chartDateType = ChartDateType.onGoing;
                timeSettings.duration.intervalType = TimeInterval.year1;
                timeSettings.toCurrentDate = true;
                break;
            case TimePeriod.l12M:
                timeSettings.chartDateType = ChartDateType.lastToCurrentTime;
                timeSettings.duration.intervalType = TimeInterval.month1;
                timeSettings.duration.intervalCount = 12;
                break;
        }
        return timeSettings;
    }

    export class ShowTimeIntervalParam {
        public showSeconds?: boolean = true;
        public showWeeks?: boolean = true;
        public showMonths?: boolean = true;
        public showQuarters?: boolean = true;
        public showYears?: boolean = true;
    }

    type IntervalTypeDesc = {
        value: TimeInterval;
        isTime: boolean;
        name: string;
        pluralName: string;
        order: number;
        displayName: string;
    };
    const intervalTypesByValue: _.Dictionary<IntervalTypeDesc> = [
        {
            value: TimeInterval.default,
            isTime: false,
            displayName: '',
            name: strings.timeDrillDownType.default,
            pluralName: strings.timeDrillDownType.default,
            order: 20
        },
        {
            value: TimeInterval.custom,
            isTime: false,
            displayName: '',
            name: strings.timeDrillDownType.year,
            pluralName: strings.timeDrillDownType_plural.years,
            order: 20
        }, // ???
        {
            value: TimeInterval.second1,
            isTime: true,
            displayName: strings.timeIntervalTypes.second1,
            name: strings.timeDrillDownType.second,
            pluralName: strings.timeDrillDownType_plural.seconds,
            order: 0
        },
        {
            value: TimeInterval.minute1,
            isTime: true,
            displayName: strings.timeIntervalTypes.minute1,
            name: strings.timeDrillDownType.minute,
            pluralName: strings.timeDrillDownType_plural.minutes,
            order: 2
        },
        {
            value: TimeInterval.hour1,
            isTime: true,

            displayName: strings.timeIntervalTypes.hour1,
            name: strings.timeDrillDownType.hour,
            pluralName: strings.timeDrillDownType_plural.hours,
            order: 6
        },
        {
            value: TimeInterval.day1,
            isTime: true,
            displayName: strings.timeIntervalTypes.day1,
            name: strings.timeDrillDownType.day,
            pluralName: strings.timeDrillDownType_plural.days,
            order: 10
        },
        {
            value: TimeInterval.week1,
            isTime: true,
            displayName: strings.timeIntervalTypes.week1,
            name: strings.timeDrillDownType.week,
            pluralName: strings.timeDrillDownType_plural.weeks,
            order: 11
        },
        {
            value: TimeInterval.month1,
            isTime: true,
            displayName: strings.timeIntervalTypes.month1,
            name: strings.timeDrillDownType.month,
            pluralName: strings.timeDrillDownType_plural.months,
            order: 12
        },
        {
            value: TimeInterval.quarter1,
            isTime: true,
            displayName: strings.timeIntervalTypes.quarter1,
            name: strings.timeDrillDownType.quarter,
            pluralName: strings.timeDrillDownType_plural.quarters,
            order: 13
        },
        {
            value: TimeInterval.year1,
            isTime: true,
            displayName: strings.timeIntervalTypes.year1,
            name: strings.timeDrillDownType.year,
            pluralName: strings.timeDrillDownType_plural.years,
            order: 14
        }
    ].searchTree(desc => desc.value);
}

export function dateTimeInterval(from: Date, to: Date): DateTimeInterval {
    const res = DateTimeInterval.create();
    res.from = from;
    res.to = to;
    return res;
}

export function dateTimeDuration(intervalType: TimeInterval, intervalCount = 1): DateTimeDuration {
    const res = DateTimeDuration.create();
    res.intervalType = intervalType;
    res.intervalCount = intervalCount;
    return res;
}

export function emptyTimeDuration(): DateTimeDuration {
    return dateTimeDuration(TimeInterval.default);
}

export function getCurrentDateOffset(yearsOffset: number, monthOffset = 0, dayOffset = 0): Date {
    const cur = new Date();
    return new Date(cur.getFullYear() + yearsOffset, cur.getMonth() + monthOffset, cur.getDate() + dayOffset);
}

export function millisecondsToMinutes(milliseconds: number): number{
    return milliseconds / 1000 / 60;
}

export function millisecondsToHours(milliseconds: number): number {
    return milliseconds / 1000 / 60 / 60;
}

export function millisecondsToDays(milliseconds: number): number {
    return milliseconds / 1000 / 60 / 60 / 24;
}

export function durationToMilliseconds(duration: DateTimeDuration): number {
    let mSecs = 0;
    switch (duration.intervalType) {
        case TimeInterval.day1:
            mSecs = Math.trunc(duration.intervalCount * 24 * 60 * 60 * 1000);
            break;
        case TimeInterval.hour1:
            mSecs = Math.trunc(duration.intervalCount * 60 * 60 * 1000);
            break;
        case TimeInterval.minute1:
            mSecs = Math.trunc(duration.intervalCount * 60 * 1000);
            break;
        case TimeInterval.second1:
            mSecs = Math.trunc(duration.intervalCount * 1000);
            break;
    }
    return mSecs;
}

export default timeHelper;
